import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import {
  apiGetPatientsPatientIdComputed,
  PermissionBit,
  QuickLookTasks,
} from "../../../api";
import { AppModel } from "../../../app/app-model";
import { QuickLookButton } from "../../../components/quick-look/quick-look-button";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { ppgRelatedFlag } from "../../../constants/enum-to-text";
import {
  algorithmOptions,
  algorithmPredicate,
  uniqueArrayValueOptions,
  uniqueValueOptions,
} from "../../../utils/filter-presets";
import { useFetch } from "../../../utils/use-fetch";
import { ComputedData } from "../common/computed-data";
import { PatientSubpage } from "../common/patient-subpage";
import { PlotCanvas, PlotControls, usePlot } from "../common/plot";
import { DRIPlotModel } from "./dri-computed-plot";

export const DRITable = observer(function DRITable(props: { app: AppModel }) {
  const { t } = useLingui();
  const patient = props.app.requirePatient();
  const [model] = useState(
    () =>
      new ComputedData(
        [],
        patient.api.permissions,
        patient.api.requireUserUUID(),
        patient.api,
        QuickLookTasks.ppg_auc_annotated_ql,
      ),
  );

  const { loading, refetch } = useFetch(
    async ({ isRefetch }) => {
      const response = await apiGetPatientsPatientIdComputed({
        path: { patient_id: patient.patientId },
        query: {
          all_versions: true,
          search_string: "diastolic*reserve*index",
          paginate: false,
        },
        meta: { useSessionCache: !isRefetch },
      });
      model.data = response.data.computed_data;
      model.init();
    },
    [patient.patientId],
  );

  const plot = usePlot(
    () => new DRIPlotModel(model, ["diastolic reserve index"], "plot-canvas"),
  );

  return (
    <PatientSubpage title={t`DRIs`} loading={loading}>
      <PlotCanvas model={plot} />

      <DataTable
        filterActions={<PlotControls model={plot} />}
        persistState
        editMode="dialog"
        model={model}
        itemKeyField="uuid"
        filterNoun={(count) =>
          t`${plural(count, { one: "result", other: "results" })}`
        }
        columns={[
          {
            header: <Trans>Measured</Trans>,
            attr: "measurement_datetime",
            type: "datetime",
            sort: true,
            initSort: "desc",
          },
          {
            header: <Trans>Type</Trans>,
            attr: "measurement_type",
            type: "string",
            filter: {
              kind: "multi",
              group: t`Result`,
              options: uniqueValueOptions("measurement_type"),
            },
          },
          {
            header: <Trans>Value</Trans>,
            attr: "measurement_value",
            type: "number",
            precision: 1,
          },
          {
            header: <Trans>Seerlinq algorithm</Trans>,
            attr: "seerlinq_algorithm",
            filter: {
              kind: "multi",
              group: t`Algorithm`,
              options: algorithmOptions,
              predicate: algorithmPredicate,
            },
            cell: ({ item }) =>
              item.seerlinq_algorithm + " " + item.seerlinq_algorithm_version,
          },
          {
            header: <Trans>Quality flag</Trans>,
            attr: "seerlinq_measurement_quality_flag",
            type: "enum",
            editable: true,
            filter: {
              kind: "multi",
              group: t`Result`,
              options: new Map(Object.entries(ppgRelatedFlag)),
            },
            values: ppgRelatedFlag,
          },
          {
            header: <Trans>PPG device</Trans>,
            attr: "ppgDevice",
            type: "string",
            filter: {
              kind: "multi",
              group: t`Result`,
              options: uniqueValueOptions("ppgDevice"),
            },
          },
          {
            header: <Trans>Tags</Trans>,
            attr: "tags",
            type: "array",
            filter: {
              kind: "multi",
              group: t`Result`,
              options: uniqueArrayValueOptions("tags"),
            },
          },
          {
            header: <Trans>Comment</Trans>,
            attr: "comment",
            type: "string",
            editable: true,
            multiline: true,
          },
          {
            header: <Trans>AUC Annotated QuickLook</Trans>,
            requiredPermission: PermissionBit.PPG_RAW,
            cell: ({ item }) =>
              item.ppgQuickLook ? (
                <QuickLookButton
                  label={t`QuickLook`}
                  resultLabel={t`View QuickLook`}
                  model={item.ppgQuickLook}
                  size="small"
                />
              ) : null,
          },
        ]}
        onEdit={async (editedField, item) => {
          await patient.updateItem("computed", item.uuid, editedField);
          await refetch();
        }}
      />
    </PatientSubpage>
  );
});
